import { useEffect, useState } from "react";
import { Check, X, ChevronsRight, Pencil, MessageSquarePlus } from "lucide-react";
import { supabase, pageTitle, card, text, MUTED, FAINT, ROW_LINE, NEUTRAL_BG, segWrap, segBtn, iconBtn, TEXT, INK } from "./constants";
import { useIsMobile } from "./lib/responsive";

const AdminView = ({ lang = "en", openPositionsList, setOpenPositionsList }) => {
  const [notifications, setNotifications] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [tab, setTab] = useState("notifications");
  const isMobile = useIsMobile();

  useEffect(() => {
    // Non-login notifications (JD edit requests, ref requests, etc.)
    supabase.from("admin_notifications").select("*")
      .neq("type", "login")
      .order("created_at", { ascending: false }).limit(50)
      .then(({ data }) => {
        if (!data) return;
        setNotifications(data);
        // Abrir el panel las marca como leídas (el menú quita su contador al
        // salir). En pantalla siguen en negrita hasta la próxima visita.
        const unread = data.filter(n => !n.read).map(n => n.id);
        if (unread.length) supabase.from("admin_notifications").update({ read: true }).in("id", unread).then(() => {});
      });

    // Login history for all clients
    supabase.from("admin_notifications").select("*")
      .eq("type", "login")
      .order("created_at", { ascending: false }).limit(200)
      .then(({ data }) => { if (data) setLoginHistory(data); });
  }, []);

  // El tipo lo dice el icono, siempre en gris: los estados no van por color.
  const notifIcon = (type) => {
    if (type === "candidate_validated") return <Check size={14} strokeWidth={1.75} />;
    if (type === "candidate_rejected") return <X size={14} strokeWidth={1.75} />;
    if (type === "ref_report_requested") return <ChevronsRight size={14} strokeWidth={1.75} />;
    if (type === "jd_edit_request") return <Pencil size={12} strokeWidth={1.75} />;
    if (type === "feedback") return <MessageSquarePlus size={14} strokeWidth={1.75} />;
    return "•";
  };

  // Borrar una notificación o un acceso: la X de cada fila (la tenía el menú)
  const remove = async (id, setter) => {
    const { error } = await supabase.from("admin_notifications").delete().eq("id", id);
    if (!error) setter(prev => prev.filter(x => x.id !== id));
  };
  const delBtn = (id, setter) => (
    <button onClick={() => remove(id, setter)} title={lang === "es" ? "Eliminar" : "Delete"} aria-label={lang === "es" ? "Eliminar" : "Delete"}
      style={{ ...iconBtn, color: FAINT, flexShrink: 0, ...(isMobile ? { width: 40, height: 40 } : null) }}><X size={16} strokeWidth={1.75} /></button>
  );

  const formatDate = (str) =>
    new Date(str).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(",", "");

  const sectionStyle = { ...card, padding: isMobile ? "4px 16px" : 22 };

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 860 }}>
      <div style={{ marginBottom: isMobile ? 16 : 20 }}>
        <h1 style={pageTitle(isMobile, 4)}>{lang === "es" ? "Panel de admin" : "Admin panel"}</h1>
        <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>{lang === "es" ? "Solo lo ves tú." : "Only visible to you."}</p>
      </div>

      <div style={{ ...segWrap, marginBottom: isMobile ? 16 : 24, width: isMobile ? "100%" : "fit-content", ...(isMobile ? { boxSizing: "border-box", overflowX: "auto", WebkitOverflowScrolling: "touch" } : null) }}>
        {[{ id: "notifications", label: lang === "es" ? "Notificaciones" : "Notifications" }, { id: "logins", label: lang === "es" ? "Registro de accesos" : "Login tracker" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...segBtn(tab === t.id), ...(isMobile ? { flex: "1 0 auto", minHeight: 40 } : null) }}>{t.label}</button>
        ))}
      </div>

      {tab === "notifications" && (
        <div style={sectionStyle}>
          {notifications.length === 0 ? (
            <div style={{ ...text.body, color: MUTED }}>{lang === "es" ? "Aún no hay notificaciones." : "No notifications yet."}</div>
          ) : notifications.map((n, i) => (
            <div key={n.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: i < notifications.length - 1 ? "1px solid " + ROW_LINE : "none" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: NEUTRAL_BG, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: MUTED, flexShrink: 0 }}>
                {notifIcon(n.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: isMobile ? 15 : 13, fontWeight: n.read ? 500 : (isMobile ? 800 : 700), color: n.read ? TEXT : INK, marginBottom: 2, ...(isMobile ? { overflowWrap: "anywhere" } : null) }}>{n.message || n.type}</div>
                {n.position_title && <div style={{ fontSize: isMobile ? 13 : 11, color: isMobile ? MUTED : MUTED }}>{n.position_title}</div>}
                {/* En el móvil la fecha baja a la línea de detalle: no hay ancho para otra columna */}
                {isMobile && <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>{formatDate(n.created_at)}</div>}
              </div>
              {!isMobile && (
                <div style={{ fontSize: 11, color: FAINT, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {formatDate(n.created_at)}
                </div>
              )}
              {delBtn(n.id, setNotifications)}
            </div>
          ))}
        </div>
      )}

      {tab === "logins" && (
        <div style={sectionStyle}>
          {loginHistory.length === 0 ? (
            <div style={{ ...text.body, color: MUTED }}>{lang === "es" ? "Aún no hay accesos registrados." : "No logins recorded yet."}</div>
          ) : loginHistory.map((l, i) => (
            <div key={l.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < loginHistory.length - 1 ? "1px solid " + ROW_LINE : "none", ...(isMobile ? { flexDirection: "column", alignItems: "flex-start", gap: 2, padding: "14px 0" } : null) }}>
              <div style={{ fontSize: isMobile ? 15 : 13, fontWeight: isMobile ? 700 : 600, color: INK, ...(isMobile ? { overflowWrap: "anywhere" } : null) }}>{l.user_email || "—"}</div>
              <div style={{ fontSize: isMobile ? 13 : 11, color: MUTED, textAlign: isMobile ? "left" : "right" }}>
                {formatDate(l.created_at)}
                {l.location && <span style={{ color: FAINT }}> · {l.location}</span>}
              </div>
              {l.id && delBtn(l.id, setLoginHistory)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminView;
